import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../../Auth/AuthContext";
import { getValidId } from "@SeemplicityDev/shared";
import { GET_FINDING_RAW_DATA } from "./RawDataApi";

interface UseRawDataOptions {
  isRaw?: boolean;
}

interface GetFindingRawDataResponse {
  finding?: {
    id: string;
    raw2?: string | null;
  } | null;
}

interface GetFindingRawDataVariables {
  id: string | null;
  with_extended_data: boolean;
}

// `raw2` is an arbitrary JSON string with no guaranteed shape; on a parse
// failure the error message string is stored instead, matching the original
// runtime behavior.
type ParsedRawData = unknown;

interface UseRawDataReturn {
  rawData: ParsedRawData;
  loadingRawData: boolean;
}

export const useRawData = ({ isRaw }: UseRawDataOptions): UseRawDataReturn => {
  const { id: paramsId } = useParams<{ id: string }>();
  const id = getValidId(paramsId);
  const {
    authState: { context },
  } = useContext(AuthContext);

  const [rawData, setRawData] = useState<ParsedRawData>(null);

  const { data, loading } = useQuery<
    GetFindingRawDataResponse,
    GetFindingRawDataVariables
  >(GET_FINDING_RAW_DATA, {
    variables: { id, with_extended_data: !!context?.id && !!isRaw },
    fetchPolicy: "network-only",
    skip: !id,
  });

  useEffect(() => {
    const rawData = data?.finding?.raw2;
    let parsedRawData: ParsedRawData = null;

    if (!!rawData) {
      try {
        parsedRawData = JSON.parse(rawData);
      } catch (e) {
        parsedRawData = (e as Error).message;
      }
    }

    setRawData(parsedRawData);
  }, [data]);

  return {
    rawData,
    loadingRawData: loading,
  };
};
